/**
 * Issue #278 / #292 — 学习目标分钟数（KPI考核）纯逻辑 + 文案常量。
 *
 * 区间校验 / 钳制 / 文案保持为纯函数：SettingsCard 只做真实 GET/PATCH 包装，
 * 防止把移动端首页「目标 N 分钟」重新变成前端假状态或硬编码。
 * Issue #292 口径：默认 5、区间 [1, 1000]。
 */
export const LEARNING_TARGET_MINUTES_LABEL = '目标分钟数';

/** 兜底默认（仅在后端尚未下发时占位；正式取值始终以接口返回为准）。与后端常量一致（#292）。 */
export const LEARNING_TARGET_MINUTES_DEFAULT = 5;
export const LEARNING_TARGET_MINUTES_MIN = 1;
export const LEARNING_TARGET_MINUTES_MAX = 1000;

export interface LearningTargetMinutesRange {
  min: number;
  max: number;
  defaultTargetMinutes: number;
}

/** 接口缺失时使用的兜底区间（与后端常量一致）。 */
export const DEFAULT_LEARNING_TARGET_MINUTES_RANGE: LearningTargetMinutesRange = {
  min: LEARNING_TARGET_MINUTES_MIN,
  max: LEARNING_TARGET_MINUTES_MAX,
  defaultTargetMinutes: LEARNING_TARGET_MINUTES_DEFAULT,
};

/** 草稿字符串 -> 是否为合法整数且落在区间内（非数字 / 小数 / 越界一律判否）。 */
export function isLearningTargetMinutesInRange(
  raw: string,
  range: LearningTargetMinutesRange,
): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return false;
  return n >= range.min && n <= range.max;
}

/** 把任意输入钳制到合法区间整数（与后端 clampLearningTargetMinutes 同语义，前端展示兜底）。 */
export function clampLearningTargetMinutes(
  value: number,
  range: LearningTargetMinutesRange,
): number {
  if (!Number.isFinite(value)) return range.defaultTargetMinutes;
  const intVal = Math.round(value);
  if (intVal < range.min) return range.min;
  if (intVal > range.max) return range.max;
  return intVal;
}

/** 区间提示文案（如「1-1000，默认 5」）。 */
export function learningTargetMinutesRangeHint(range: LearningTargetMinutesRange): string {
  return `${range.min}-${range.max}，默认 ${range.defaultTargetMinutes}`;
}

export function canSaveLearningTargetMinutes(args: {
  canEdit: boolean;
  pending: boolean;
  raw: string;
  range: LearningTargetMinutesRange;
}): boolean {
  if (!args.canEdit) return false;
  if (args.pending) return false;
  return isLearningTargetMinutesInRange(args.raw, args.range);
}
